import { motion } from 'framer-motion';
import { Code2, Zap, Globe, Sparkles } from 'lucide-react';

export const FeaturesShowcase = () => {
  return (
    <section className="py-24 px-4 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF6B00]/5 to-transparent" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Main Feature - AI Code Generation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Text */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/20">
                <Sparkles className="w-4 h-4 text-[#FF6B00]" />
                <span className="text-sm text-[#FF6B00] font-medium">Potenciado por IA</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                IA que entiende tu visión
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed">
                CloudTexa analiza tu descripción y genera código limpio, moderno y funcional. 
                Desde landing pages hasta aplicaciones complejas.
              </p>
            </div>
            
            {/* Right - Code Preview */}
            <div className="glass rounded-2xl p-6 border border-white/10 bg-[#0a0a0a]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="font-mono text-sm space-y-3">
                <p className="text-gray-500">// Tu prompt</p>
                <p className="text-emerald-400">"Crea un dashboard con gráficos de ventas"</p>
                <p className="text-gray-500 mt-4">// CLOUDTEXA genera</p>
                <div className="text-purple-400">
                  <p>&lt;<span className="text-blue-400">Dashboard</span>&gt;</p>
                  <p className="pl-4">&lt;<span className="text-blue-400">SalesChart</span> <span className="text-cyan-300">data</span>=<span className="text-yellow-300">{'{sales}'}</span> /&gt;</p>
                  <p className="pl-4">&lt;<span className="text-blue-400">MetricsGrid</span> <span className="text-cyan-300">metrics</span>=<span className="text-yellow-300">{'{kpis}'}</span> /&gt;</p>
                  <p>&lt;/<span className="text-blue-400">Dashboard</span>&gt;</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Editor Profesional */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Code2 className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Editor Profesional</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Monaco Editor con syntax highlighting, autocompletado y atajos de teclado.
            </p>
          </motion.div>

          {/* Preview en Tiempo Real */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Zap className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Preview en Tiempo Real</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Ve los cambios instantáneamente mientras escribes o la IA genera código.
            </p>
          </motion.div>

          {/* Multi-lenguaje */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all group"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Globe className="w-6 h-6 text-orange-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Multi-lenguaje</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              HTML, CSS, JavaScript, React, Python y más. Todo en un solo lugar.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
